import React, { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { api, CURRENCIES } from "@/lib/api";
import { toast } from "sonner";

/**
 * Reusable form dialog for Debts and Loans.
 * kind: "debt" | "loan"
 */
export default function DebtLoanFormDialog({ open, onOpenChange, initial, onSaved, kind }) {
  const isDebt = kind === "debt";
  const partyLabel = isDebt ? "Acreedor (a quién le debes)" : "Deudor (quién te debe)";
  const partyKey = isDebt ? "creditor" : "debtor";

  const EMPTY = {
    [partyKey]: "",
    description: "",
    original_amount: 0,
    remaining_amount: 0,
    interest_rate: 0,
    currency: "USD",
    due_date: null,
    status: "active",
    is_mortgage: false,
    term_months: null,
    start_date: null,
    monthly_payment: null,
    rate_type: "fixed",
  };

  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(initial || EMPTY);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!form[partyKey]?.trim()) return toast.error("Ingresa un nombre");
    setSaving(true);
    try {
      const payload = {
        ...form,
        original_amount: Number(form.original_amount) || 0,
        remaining_amount: Number(form.remaining_amount) || 0,
        interest_rate: Number(form.interest_rate) || 0,
        term_months: form.term_months ? Number(form.term_months) : null,
        monthly_payment: form.monthly_payment ? Number(form.monthly_payment) : null,
      };
      const endpoint = isDebt ? "/debts" : "/loans";
      if (initial?.id) {
        await api.put(`${endpoint}/${initial.id}`, payload);
        toast.success("Actualizado");
      } else {
        await api.post(endpoint, payload);
        toast.success("Agregado");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error("No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const dueDate = form.due_date ? new Date(form.due_date) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {initial?.id ? (isDebt ? "Editar deuda" : "Editar préstamo") : (isDebt ? "Nueva deuda" : "Nuevo préstamo")}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="col-span-2">
            <Label>{partyLabel}</Label>
            <Input data-testid="dl-party" value={form[partyKey]} onChange={(e) => set(partyKey, e.target.value)} placeholder={isDebt ? "Banco X, Juan…" : "María, Empresa Y…"} />
          </div>
          <div className="col-span-2">
            <Label>Descripción</Label>
            <Input data-testid="dl-desc" value={form.description || ""} onChange={(e) => set("description", e.target.value)} placeholder="Concepto" />
          </div>

          <div>
            <Label>Monto original</Label>
            <Input type="number" step="any" data-testid="dl-original" value={form.original_amount} onChange={(e) => set("original_amount", e.target.value)} />
          </div>
          <div>
            <Label>Monto pendiente</Label>
            <Input type="number" step="any" data-testid="dl-remaining" value={form.remaining_amount} onChange={(e) => set("remaining_amount", e.target.value)} />
          </div>

          <div>
            <Label>Tasa de interés (%)</Label>
            <Input type="number" step="any" data-testid="dl-rate" value={form.interest_rate} onChange={(e) => set("interest_rate", e.target.value)} />
          </div>
          <div>
            <Label>Moneda</Label>
            <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
              <SelectTrigger data-testid="dl-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isDebt && (
            <>
              <div>
                <Label>Tipo de interés</Label>
                <Select value={form.rate_type || "fixed"} onValueChange={(v) => set("rate_type", v)}>
                  <SelectTrigger data-testid="dl-rate-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Fija</SelectItem>
                    <SelectItem value="variable">Variable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cuota mensual (opcional)</Label>
                <Input
                  type="number"
                  step="any"
                  data-testid="dl-monthly-payment"
                  value={form.monthly_payment ?? ""}
                  onChange={(e) => set("monthly_payment", e.target.value === "" ? null : e.target.value)}
                  placeholder="Se calcula si se deja vacío"
                />
              </div>
            </>
          )}

          <div>
            <Label>Fecha de vencimiento</Label>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  data-testid="dl-duedate"
                  type="button"
                  className="w-full flex items-center gap-2 px-3 h-10 rounded-md border border-input bg-background text-sm text-left"
                >
                  <CalendarIcon className="w-4 h-4 text-[#8A9992]" />
                  <span className={dueDate ? "text-[#0A110E]" : "text-[#8A9992]"}>
                    {dueDate ? format(dueDate, "PPP") : "Selecciona una fecha"}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white" align="start">
                <Calendar
                  mode="single"
                  selected={dueDate}
                  onSelect={(d) => set("due_date", d ? d.toISOString() : null)}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div>
            <Label>Estado</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="dl-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Activo</SelectItem>
                <SelectItem value="paid">Pagado</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isDebt && (
            <div className="col-span-2 border-t border-[#EBEBEA] pt-4 mt-2">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">Hipoteca / Amortización</div>
                  <div className="text-xs text-[#8A9992] mt-1">Completa para ver el cuadro de amortización</div>
                </div>
                <label className="flex items-center gap-2 text-sm text-[#5F7368] cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="dl-is-mortgage"
                    checked={!!form.is_mortgage}
                    onChange={(e) => set("is_mortgage", e.target.checked)}
                    className="w-4 h-4 accent-[#1C7C54]"
                  />
                  Es una hipoteca
                </label>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Plazo (meses)</Label>
                  <Input
                    type="number"
                    step="1"
                    data-testid="dl-term-months"
                    value={form.term_months ?? ""}
                    onChange={(e) => set("term_months", e.target.value === "" ? null : e.target.value)}
                    placeholder="360"
                  />
                </div>
                <div>
                  <Label>Fecha de inicio</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        data-testid="dl-start-date"
                        type="button"
                        className="w-full flex items-center gap-2 px-3 h-10 rounded-md border border-input bg-background text-sm text-left"
                      >
                        <CalendarIcon className="w-4 h-4 text-[#8A9992]" />
                        <span className={form.start_date ? "text-[#0A110E]" : "text-[#8A9992]"}>
                          {form.start_date ? format(new Date(form.start_date), "PPP") : "Selecciona una fecha"}
                        </span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-white" align="start">
                      <Calendar
                        mode="single"
                        selected={form.start_date ? new Date(form.start_date) : null}
                        onSelect={(d) => set("start_date", d ? d.toISOString() : null)}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button data-testid="dl-save-btn" onClick={submit} disabled={saving} className="bg-[#0F291E] hover:bg-[#1C7C54]">
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
